import React, { useContext, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Typography } from '@mui/material';
import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_REQUIRED,
    DocTypeEnumType,
    DossierJsonldRead,
} from '@europrocurement/l2d-domain';
import { castIriToId, getFileExtension } from '@europrocurement/l2d-utils';
import { useModal } from '@europrocurement/flexy-components';
import {
    type FproDossierDocJsonldDocumentfileRead,
    type TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead,
    type FproDosDocFileJsonldDocumentfileRead,
    type FichiersDossierJsonldDocumentfileRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    ADD_DOS_DOC_CTX,
    ADD_DOS_DOC_FILE_CTX,
    REMOVE_DOS_DOC_CTX,
    REMOVE_DOS_DOC_FILE_CTX,
    UPDATE_DOS_DOC_CTX,
    UPDATE_DOS_DOC_FILE_CTX,
    useDossierDocumentsCtx,
    useDossierDocumentsCtxDispatch,
} from '@europrocurement/l2d-modules/providers';
import DocumentPreviewContext from '../providers/DocumentPreviewContext';
import UpdateFileTypeDocModal from '../components/shared/UpdateFileTypeDocModal/UpdateFileTypeDocModal';
import { type CreatedLiasseType, type DocumentPreview } from '../providers/types';
import useDossierDocumentFilesApi, {
    type DocumentExtension,
    type DossierFichier,
} from './useDossierDocumentFilesApi';
import useFileChecker from './useFileChecker';
import { useCurrentDossierCtx } from '../providers/CurrentDossierProvider';
import UploadProgressSnackbar from '../components/shared/UploadProgressSnackbar/UploadProgressSnackbar';

type UseDossierFileProps = {
    typeDoc?: typeof DOC_TYPE_REQUIRED | typeof DOC_TYPE_INTERNAL | typeof DOC_TYPE_DOWNLOADABLE;
    setIsSubmitting?: React.Dispatch<React.SetStateAction<boolean>>;
    dossierProps?: DossierJsonldRead;
    source?: 'FRONT' | 'BACK';
    displayPreviewOnAddFile?: boolean;
};

type TypePromiseUpdateFichier = {
    success: boolean;
    files?: FichiersDossierJsonldDocumentfileRead[];
};

const useDossierFile = ({
    typeDoc = DOC_TYPE_REQUIRED,
    setIsSubmitting = () => {},
    dossierProps,
    displayPreviewOnAddFile = true,
}: UseDossierFileProps) => {
    const numberFilesChanged = useRef(0);
    const [submittingItemId, setSubmittingItemId] = useState<number | 'uploader' | null>(null);
    const isSubmittingRef = useRef(false);
    const uploadProgressIncrementRef = useRef<(() => void) | null>(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { setDocumentPreview, documentPreview } = useContext(DocumentPreviewContext);
    const { dossier } = useCurrentDossierCtx();
    const { modalActions } = useModal();
    const {
        getDosDocFileContent,
        uploadDosDocFile,
        uploadAdditionnalDocDosFile,
        updateDosDocFile,
        removeDosDocFile,
        postDosDoc,
        removeDosDoc,
        downloadDosDocFile,
        isFileSupported,
    } = useDossierDocumentFilesApi();
    const { dosDocs, getDossierDocumentFileById, getDossierDocumentById } =
        useDossierDocumentsCtx();
    const dossierDocumentDispatch = useDossierDocumentsCtxDispatch();
    const { displayFilesCheckerForm } = useFileChecker();

    const handleFilePreview = async ({
        fichier,
        document,
        dossierId,
    }: {
        fichier: DossierFichier;
        document:
            | TypeDeDocumentDesDossiersJsonldTypedocumentdossierRead
            | FproDossierDocJsonldDocumentfileRead;
        dossierId?: number;
    }) => {
        setIsSubmitting(true);
        const file = await getDosDocFileContent(fichier.id as number);
        // Dossier from props, document or from selectedDossier
        let fdosId = dossierId;
        if (!fdosId) {
            fdosId = castIriToId(
                'dossier' in document && document.dossier
                    ? (document.dossier as string)
                    : (dossier!['@id'] as string),
            );
        }
        const statusId = fichier?.statut?.id
            ? fichier?.statut?.id
            : castIriToId(document.statut as string);
        const fileName = fichier.fichier as string;
        const ext = getFileExtension(fileName);

        const newDocumentPreview = {
            fileName,
            fileType: ext as DocumentExtension,
            dossierId: fdosId,
            fichierId: fichier?.id as number,
            statusId,
            type: document?.document?.type as number,
            expediteurType: fichier?.expediteur ? fichier?.expediteur : typeDoc,
            documentLabel: document.libelle as string,
            documentTypeId: document.id,
            content: file.data as unknown as Blob,
        };

        if (!isFileSupported(newDocumentPreview.fileType as DocumentExtension)) {
            const fichierSearchSupport = getDossierDocumentFileById(fichier.id as number);
            if (fichierSearchSupport) {
                await downloadDosDocFile(fichierSearchSupport);
            }
        }

        setDocumentPreview(newDocumentPreview);
        setIsSubmitting(false);
    };

    const handleAddFile = async (
        dossierId: number,
        documentId: number,
        file: File,
        fichierId?: number,
    ) => {
        if (isSubmittingRef.current) return;
        if (!dosDocs) {
            enqueueSnackbar(
                <Typography>
                    Une erreur est survenue lors de la récupération des documents existants
                </Typography>,
                { variant: 'error' },
            );
            return;
        }
        const document = getDossierDocumentById(documentId);

        if (!document) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });

            return;
        }
        isSubmittingRef.current = true;
        setSubmittingItemId(documentId);
        setIsSubmitting(true);
        let existingFichier = null;
        if (fichierId) {
            existingFichier = getDossierDocumentFileById(fichierId as number);
        }

        if (existingFichier) {
            removeDosDocFile(existingFichier.id as number);
        }

        try {
            const fichiersRes = await uploadDosDocFile(
                dossierId,
                `/formalite/types-documents/${document?.document?.id}` as string,
                file,
                typeDoc,
                undefined,
                false,
                false,
            );
            if (fichiersRes.files && fichiersRes.files.length > 0) {
                if (fichiersRes.files.length === 1) {
                    const newFile = fichiersRes.files[0];

                    if (existingFichier) {
                        dossierDocumentDispatch({
                            type: UPDATE_DOS_DOC_FILE_CTX,
                            payload: { dosDocId: documentId, fichier: newFile },
                        });
                    } else {
                        dossierDocumentDispatch({
                            type: ADD_DOS_DOC_FILE_CTX,
                            payload: { dosDocId: documentId, fichier: newFile },
                        });

                        if (newFile.typeDocDossier?.id && displayPreviewOnAddFile) {
                            // Display document preview on single file upload
                            await handleFilePreview({
                                fichier: newFile,
                                document: newFile.typeDocDossier,
                                dossierId,
                            });
                        }
                    }
                    enqueueSnackbar(<Typography>Votre fichier a bien été ajouté</Typography>, {
                        variant: 'success',
                    });
                    numberFilesChanged.current += 1;
                } else {
                    // Vérifier si le type de doc courant correpond à un des résultats envoyés,
                    // dans ce cas on vient mettre a jour le fichier
                    fichiersRes.files.forEach(() => {
                        numberFilesChanged.current += 1;
                    });
                    enqueueSnackbar(<Typography>Vos fichiers ont bien été ajoutés</Typography>, {
                        variant: 'success',
                    });

                    displayFilesCheckerForm(
                        [{ originalFile: file, createdFiles: fichiersRes.files }],
                        getDosDocFileContent,
                    );
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors du transfert de votre fichier</Typography>,
                {
                    variant: 'error',
                },
            );
        } finally {
            isSubmittingRef.current = false;
            setSubmittingItemId(null);
            setIsSubmitting(false);
        }
    };

    const handleRemoveFile = async (fichierId: number) => {
        if (isSubmittingRef.current) return;
        const fichier = getDossierDocumentFileById(fichierId as number);

        if (!fichier) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, {
                variant: 'error',
            });

            return;
        }
        isSubmittingRef.current = true;
        setSubmittingItemId(fichierId);
        setIsSubmitting(true);
        try {
            await removeDosDocFile(fichierId);
            dossierDocumentDispatch({
                type: REMOVE_DOS_DOC_FILE_CTX,
                payload: { fichierId },
            });

            // Close document preview if the current file is deleted
            if (documentPreview && documentPreview.fichierId === fichierId) {
                setDocumentPreview(undefined);
            }

            enqueueSnackbar(<Typography>Votre fichier a bien été supprimé</Typography>, {
                variant: 'success',
            });
        } catch (error) {
            console.error(error);
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, {
                variant: 'error',
            });
        } finally {
            isSubmittingRef.current = false;
            setSubmittingItemId(null);
            setIsSubmitting(false);
        }
    };

    const handleShow = async (label: string, fichierId: number, documentId: number) => {
        if (isSubmittingRef.current) return;
        const fichier = getDossierDocumentFileById(fichierId as number);

        if (!fichier) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });

            return;
        }
        isSubmittingRef.current = true;
        setSubmittingItemId(documentId);
        setIsSubmitting(true);
        try {
            if (!dosDocs) {
                throw new Error('No documents found');
            }

            const document = getDossierDocumentById(documentId);

            if (!document) {
                throw new Error('No document found');
            }

            await handleFilePreview({ fichier, document });
        } catch (err) {
            console.log(err);
            enqueueSnackbar(`Une erreur est survenue lors de l'affichage du document ${label}`, {
                variant: 'error',
            });
        } finally {
            isSubmittingRef.current = false;
            setSubmittingItemId(null);
            setIsSubmitting(false);
        }
    };

    const handleDownload = async (fichierId: number) => {
        if (isSubmittingRef.current) return;
        const fichier = getDossierDocumentFileById(fichierId as number);

        if (!fichier) {
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });

            return;
        }
        isSubmittingRef.current = true;
        setSubmittingItemId(fichierId);
        setIsSubmitting(true);
        try {
            await downloadDosDocFile(fichier);
        } catch (err) {
            console.error(err);
            enqueueSnackbar(<Typography>Une erreur est survenue</Typography>, { variant: 'error' });
        } finally {
            isSubmittingRef.current = false;
            setSubmittingItemId(null);
            setIsSubmitting(false);
        }
    };

    const handleAdditionalUpload = async (file: File) => {
        const dossierId = dossier?.id ? dossier.id : dossierProps?.id;

        if (!dossierId) {
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors du transfert de votre fichier</Typography>,
                {
                    variant: 'error',
                },
            );
            throw new Error('Dossier manquant');
        }

        const fichier = await uploadAdditionnalDocDosFile(
            dossierId as number,
            file,
            typeDoc,
            5,
            false,
            false,
        );
        numberFilesChanged.current += 1;
        return fichier;
    };

    const handleAdditionalUploads = async (files: FileList) => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setSubmittingItemId('uploader');
        setIsSubmitting(true);

        const totalFiles = files.length;
        const progressKey = enqueueSnackbar(
            <UploadProgressSnackbar
                total={totalFiles}
                onMount={(increment) => {
                    uploadProgressIncrementRef.current = increment;
                }}
            />,
            { variant: 'info', persist: true },
        );

        let filesResponse: FproDosDocFileJsonldDocumentfileRead[][] = [];
        try {
            // DO not execute a Promise.all in post file, the back will add the file in an available dos_doc_type,
            // we have to wait that the dos_doc is update before to send an new file or it can add multiple file in the same doc (not persist quickly enough)
            const newFichiers = await Array.from(files).reduce(
                async (prevPromise, file) => {
                    const acc = await prevPromise;
                    const result = await handleAdditionalUpload(file);
                    uploadProgressIncrementRef.current?.();
                    return [...acc, result];
                },
                Promise.resolve([] as Awaited<ReturnType<typeof handleAdditionalUpload>>[]),
            );

            filesResponse = newFichiers
                .filter((fichierPost) => fichierPost.files !== undefined)
                .map((fichierPost) => fichierPost.files as FproDosDocFileJsonldDocumentfileRead[]);

            // Update documents context
            // DosDoc and dosDocFiles rebuild from dosDocFile response
            // The filesResponse may have created a new dosDoc on file creation or set the file in an existing dosDoc
            filesResponse.flat().map((newFile) => {
                // check if doc is allready existing
                const existingDosDoc = dosDocs?.find(
                    (dosDoc) => newFile.typeDocDossier && dosDoc.id === newFile.typeDocDossier.id,
                );
                if (existingDosDoc) {
                    return dossierDocumentDispatch({
                        type: UPDATE_DOS_DOC_CTX,
                        payload: {
                            ...newFile.typeDocDossier,
                            dossier: newFile.dossier,
                            statut: newFile.statut,
                            fichiers: [newFile],
                        },
                    });
                }
                return dossierDocumentDispatch({
                    type: ADD_DOS_DOC_CTX,
                    payload: {
                        ...newFile.typeDocDossier,
                        dossier: newFile.dossier,
                        statut: newFile.statut,
                        fichiers: [newFile],
                    },
                });
            });

            closeSnackbar(progressKey);
            enqueueSnackbar(<Typography>Vos fichiers ont bien été ajoutés</Typography>, {
                variant: 'success',
            });
        } catch (err) {
            console.error(err);
            closeSnackbar(progressKey);
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors du transfert de vos fichiers</Typography>,
                {
                    variant: 'error',
                },
            );
        } finally {
            if (filesResponse) {
                // check forEach fileResponse if multiple files
                const arrayOfLiasse: CreatedLiasseType[] = [];
                // check if multiples files have been generated of an entry and push the object of original and created files
                filesResponse.forEach((createdFiles, index) => {
                    if (createdFiles.length > 1) {
                        arrayOfLiasse.push({
                            originalFile: files[index],
                            createdFiles,
                        });
                    }
                });
                if (arrayOfLiasse.length > 0) {
                    // files checker modal
                    displayFilesCheckerForm(arrayOfLiasse, getDosDocFileContent);
                }
            }

            isSubmittingRef.current = false;
            setSubmittingItemId(null);
            setIsSubmitting(false);
        }
    };

    const handleUpdateDosDocFile = async (
        fichierId: number,
        payload: Record<string, unknown>,
        removeFileFromPreviousDosDoc?: boolean,
        displaySuccessSnackBar: boolean = true,
    ): Promise<TypePromiseUpdateFichier> => {
        setIsSubmitting(true);

        const res = await updateDosDocFile(fichierId, payload);

        if (res.status === 200) {
            let updatedFile: FproDosDocFileJsonldDocumentfileRead | undefined = res.data.files
                ? res.data.files[0]
                : undefined;
            if (res.data.files && res.data.files?.length > 1) {
                updatedFile = res.data.files.find((file) => file.id === fichierId);
            }
            if (updatedFile && documentPreview && documentPreview.fichierId === updatedFile.id) {
                setDocumentPreview({
                    ...documentPreview,
                    documentLabel: updatedFile.typeDocDossier?.libelle as string,
                    documentTypeId: updatedFile.typeDocDossier?.id,
                    statusId: updatedFile.statut?.id,
                    type: updatedFile.typeDocDossier?.document?.type as number,
                });
            }
            if (updatedFile && updatedFile.typeDocDossier) {
                // Update conetext state
                if (res.data.files && res.data.files.length > 1) {
                    res.data.files.forEach((file) => {
                        dossierDocumentDispatch({
                            type: UPDATE_DOS_DOC_FILE_CTX,
                            payload: {
                                dosDocId: file.typeDocDossier!.id as number,
                                fichier: file,
                                removeFileFromPreviousDosDoc,
                            },
                        });
                    });
                } else if (res.data.files && res.data.files.length === 1) {
                    dossierDocumentDispatch({
                        type: UPDATE_DOS_DOC_FILE_CTX,
                        payload: {
                            dosDocId: updatedFile.typeDocDossier.id as number,
                            fichier: updatedFile,
                            removeFileFromPreviousDosDoc,
                        },
                    });
                }
                if (displaySuccessSnackBar) {
                    enqueueSnackbar('Modification effectuée');
                }
                setIsSubmitting(false);
                return { success: true, files: res.data.files };
            }
            enqueueSnackbar('Une erreur est survenue lors de la modification de fichier');
            setIsSubmitting(false);
            return { success: false };
        }
        enqueueSnackbar('Une erreur est survenue lors de la modification de fichier');
        setIsSubmitting(false);
        return { success: false };
    };

    const handleTypageDosDocFileIaDoc = async (fichierId: number) => {
        const { files } = await handleUpdateDosDocFile(
            fichierId as number,
            {
                typageAuto: true,
                deliassageAuto: false,
            },
            false,
            false,
        );

        if (files && files.length === 1) {
            const newFile = files[0];

            // check if iadoc has found an other type
            if (newFile.typedocIADoc && newFile.typedocIADoc.id !== newFile.typeDocDossier?.id) {
                // Get IADoc type
                // check if doc allready exist and it has no files linked
                const existingNewDosDoc = dosDocs?.find(
                    (dosDoc) =>
                        newFile.typedocIADoc &&
                        (!dosDoc.fichiers || dosDoc.fichiers.length === 0) &&
                        dosDoc.document?.id === newFile.typedocIADoc?.id,
                );

                if (existingNewDosDoc) {
                    // Update dosDoc file and remove file from previous doc
                    await handleUpdateDosDocFile(
                        fichierId as number,
                        { typeDocDossier: existingNewDosDoc.id },
                        true,
                        false,
                    );
                    return enqueueSnackbar('Type modifié');
                }

                // Create new DosDoc and update dosDocFile
                if (newFile.dossier && newFile.typedocIADoc && newFile.statut) {
                    // Creation of a new dosDocType with the type found by IaDoc
                    const newDosDoc = await postDosDoc(
                        castIriToId(newFile.dossier),
                        newFile.typedocIADoc.id as number,
                        newFile.typedocIADoc.libelle as string,
                        newFile.statut.id as number,
                    );

                    if (newDosDoc) {
                        dossierDocumentDispatch({
                            type: ADD_DOS_DOC_CTX,
                            payload: {
                                ...newDosDoc,
                                dossier: newFile.dossier,
                                statut: newFile.statut,
                                fichiers: [newFile],
                            },
                        });
                        // Update dosDoc file and remove file from previous doc
                        await handleUpdateDosDocFile(
                            fichierId as number,
                            { typeDocDossier: newDosDoc.id },
                            true,
                            false,
                        );

                        return enqueueSnackbar('Type modifié');
                    }
                }
                return enqueueSnackbar(
                    'Une erreur est survenue lors de la création du nouveau type de document',
                    {
                        variant: 'info',
                        autoHideDuration: 2000,
                    },
                );
            }
            return enqueueSnackbar('Aucun type trouvé pour le document', {
                variant: 'info',
                autoHideDuration: 2000,
            });
        }
        return enqueueSnackbar('Une erreur est survenue lors du typage de document', {
            variant: 'info',
            autoHideDuration: 2000,
        });
    };

    // soft delete dosDoc and remove document from context
    const handleRemoveDocument = async (documentId: number) => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setSubmittingItemId(documentId);
        setIsSubmitting(true);
        try {
            const result = await removeDosDoc(documentId);
            if ([200, 201, 204].includes(result.status)) {
                // Remove document from the context
                dossierDocumentDispatch({
                    type: REMOVE_DOS_DOC_CTX,
                    payload: documentId,
                });
            } else {
                throw new Error(
                    'Une erreur est survenue lors de la modification du type de document',
                );
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(
                <Typography>Une erreur est survenue lors de la suppression du document</Typography>,
                {
                    variant: 'error',
                },
            );
            throw new Error('Une erreur est survenue lors de la modification du type de document');
        } finally {
            isSubmittingRef.current = false;
            setSubmittingItemId(null);
            setIsSubmitting(false);
        }
    };

    const handleMoveFile = (label: string, fichierId: number) => {
        modalActions.call(
            <UpdateFileTypeDocModal
                label={label}
                fichierId={fichierId}
                typeDoc={typeDoc}
                actionOnValidation={async (fichierIdParam, typeDocPram) => {
                    await handleUpdateDosDocFile(fichierIdParam, { expediteur: typeDocPram });
                    setDocumentPreview(undefined);
                }}
            />,
        );
    };

    const getFilePreview = async (
        documentId: number,
        fichierId: number,
    ): Promise<DocumentPreview> => {
        const document = getDossierDocumentById(documentId);
        if (!document) {
            throw new Error('No document found');
        }

        const fichier = getDossierDocumentFileById(fichierId as number);
        if (!fichier) {
            throw new Error('No file found');
        }

        const file = await getDosDocFileContent(fichier.id as number);
        const dossierId = castIriToId(document?.dossier as string);
        const statusId = fichier?.statut?.id
            ? fichier?.statut?.id
            : castIriToId(document.statut as string);
        const fileName = fichier.fichier as string;
        const ext = getFileExtension(fileName);

        return {
            fileName,
            fileType: ext as DocumentExtension,
            dossierId,
            fichierId: fichier?.id as number,
            statusId,
            type: document?.document?.type as number,
            expediteurType: fichier?.expediteur as DocTypeEnumType,
            documentLabel: document.libelle as string,
            documentTypeId: document.id,
            content: file.data as unknown as Blob,
        };
    };

    const getFileExtensionFromFileId = (fichierId: number) => {
        const fichier = getDossierDocumentFileById(fichierId as number);

        if (!fichier) {
            throw new Error('No file found');
        }

        return getFileExtension(fichier.fichier as string);
    };

    const handleDeliassage = async (fichierId: number) => {
        const { files } = await handleUpdateDosDocFile(
            fichierId,
            { typageAuto: true, deliassageAuto: true },
            false,
            false,
        );

        if (!files) {
            return { files: undefined };
        }

        const newFiles = files.filter((f) => f.id !== fichierId);

        newFiles.forEach((newFile) => {
            const existingDosDoc = dosDocs?.find(
                (dosDoc) => newFile.typeDocDossier && dosDoc.id === newFile.typeDocDossier.id,
            );
            if (existingDosDoc) {
                dossierDocumentDispatch({
                    type: UPDATE_DOS_DOC_CTX,
                    payload: {
                        ...newFile.typeDocDossier,
                        dossier: newFile.dossier,
                        statut: newFile.statut,
                        fichiers: [newFile],
                    },
                });
            } else {
                dossierDocumentDispatch({
                    type: ADD_DOS_DOC_CTX,
                    payload: {
                        ...newFile.typeDocDossier,
                        dossier: newFile.dossier,
                        statut: newFile.statut,
                        fichiers: [newFile],
                    },
                });
            }
        });

        return { files: newFiles };
    };

    return {
        handleAddFile,
        handleAdditionalUploads,
        handleDeliassage,
        handleMoveFile,
        handleUpdateDosDocFile,
        handleTypageDosDocFileIaDoc,
        handleRemoveFile,
        handleShow,
        getFilePreview,
        handleDownload,
        handleRemoveDocument,
        getFileExtensionFromFileId,
        numberFilesChanged,
        submittingItemId,
    };
};

export default useDossierFile;
